import React, { useEffect, useState } from "react";
import { UsersIcon } from "@heroicons/react/solid";
import { collection, addDoc, getDocs } from "firebase/firestore";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import Button from "../Atoms/Button";
import Input from "../Atoms/Input";
import TextArea from "../Atoms/TextArea";
import TestimonialCard from "../Molecules/TestimonialCard";
import { db } from "../../firebase";
import HeroIcon from "../Atoms/HeroIcon";

interface Testimonial {
  id: string;
  title: string;
  description: string;
}

interface TestimonialsProps {}

const Testimonials: React.FC<TestimonialsProps> = () => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [testimonialList, setTestimonialList] = useState<Testimonial[]>([]);
  const maxChars = 120;
  useEffect(() => {
    const fetchTestimonials = async () => {
      const querySnapshot = await getDocs(collection(db, "testimonials"));
      const testimonialsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Testimonial[];
      setTestimonialList(testimonialsData);
    };

    fetchTestimonials();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newTestimonial = {
      // id: "",
      title: title,
      description: description,
    };
    try {
      const docRef = await addDoc(
        collection(db, "testimonials"),
        newTestimonial,
      );

      const testimonialWithId: Testimonial = {
        ...newTestimonial,
        id: docRef.id,
      };

      setTitle("");
      setDescription("");

      Swal.fire({
        icon: "success",
        title: t("testimonials.successTitle"),
        text: t("testimonials.successMessage"),
      }).then(() => {
        setTestimonialList((prev) => [...prev, testimonialWithId]);
      });
    } catch (error) {
      console.error("woopp error to send testimonial.");
      Swal.fire({
        icon: "error",
        title: t("testimonials.errorTitle"),
        text: t("testimonials.errorMessage"),
      });
    }
  };
  return (
    <section id="testimonials">
      <div className="container px-5 py-10 mx-auto text-center">
        <HeroIcon Icon={UsersIcon} className="w-10 inline-block mb-4" />
        <h1 className="sm:text-4xl text-3xl font-medium title-font text-white mb-4">
          {t("testimonials.title")}
        </h1>
        <p className="text-base leading-relaxed  lg:w-3/4 mx-auto text-white">
          {t("testimonials.description")}
        </p>
      </div>
      <div className="flex flex-wrap lg:w-4/5 sm:mx-auto sm:mb-2 mx-2">
        <TestimonialCard testimonials={testimonialList} />
      </div>
      <div className="col-md-12">
        <form onSubmit={handleSubmit} className="mb-8">
          <div className="flex flex-col items-center justify-center p-5 mb-4 text-center">
            <p className="mb-2 text-2xl font-medium title-font text-white">
              {t("testimonials.leaveComment")}
            </p>
            <Input
              id="name"
              name="testimonial_name"
              type="text"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
              placeholder={t("testimonials.namePlaceholder")}
              className="w-full sm:w-80 bg-gray-900 rounded border border-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-900 text-base outline-none text-gray-100 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
              required
            />
            <TextArea
              id="description"
              name="testimonial_description"
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const value = e.target.value;
                if (value.length <= maxChars) {
                  setDescription(value);
                }
              }}
              placeholder={t("testimonials.descriptionPlaceholder")}
              className="w-full sm:w-80 bg-gray-900 rounded border border-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-900 text-base outline-none text-gray-100 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
              rows={3}
              required
            />
            <p className="text-sm text-gray-400 mt-1">
              {description.length}/{maxChars} {t("testimonials.characters")}
            </p>
            <Button
              type="submit"
              className="bg-green-500 text-white border-0 py-2 px-6 mt-4 hover:bg-indigo-600"
              titleButton={t("testimonials.submit")}
            />
          </div>
        </form>
      </div>
    </section>
  );
};

export default Testimonials;
